import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Info, Palette, Trash2, Shield, Check, Loader2 } from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../context/ToastContext';

interface AgencyDomain {
  id: number;
  domain: string;
  status: string;
  cloudflare_proxy_enabled: boolean;
  cname_target: string | null;
  dns_validated_at: string | null;
  ssl_issued_at: string | null;
  last_validation_check: string | null;
  validation_errors: string | null;
  logo_url: string | null;
  primary_color: string | null;
  favicon_url: string | null;
  created_at: string;
  updated_at: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  agencyId: string;
  agencyName: string;
}

const AgencyDomainsModal: React.FC<Props> = ({ isOpen, onClose, agencyId, agencyName }) => {
  const { showSuccess, showError } = useToast();
  const [domains, setDomains] = useState<AgencyDomain[]>([]);
  const [loading, setLoading] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<AgencyDomain | null>(null);
  const [showBrandingForm, setShowBrandingForm] = useState(false);
  const [branding, setBranding] = useState({
    logo_url: '',
    primary_color: '',
    favicon_url: ''
  });

  // Hover/focus states
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [focusedInput, setFocusedInput] = useState<string | null>(null);
  const [hoveredDomain, setHoveredDomain] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadDomains();
    }
  }, [isOpen, agencyId]);

  const loadDomains = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/agencies/${agencyId}/domains`);
      setDomains(response.data);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to load domains');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) {
      showError('Please enter a domain');
      return;
    }

    const domainRegex = /^[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,}$/i;
    if (!domainRegex.test(newDomain)) {
      showError('Invalid domain format');
      return;
    }

    try {
      setAdding(true);
      await apiService.post(`/agencies/${agencyId}/domains`, { domain: newDomain });
      showSuccess(`Domain ${newDomain} added successfully`);
      setNewDomain('');
      loadDomains();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to add domain');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveDomain = async (domainId: number) => {
    if (!window.confirm('Are you sure you want to remove this domain? This will also remove DNS records from Cloudflare.')) {
      return;
    }

    try {
      await apiService.delete(`/agencies/${agencyId}/domains/${domainId}`);
      showSuccess('Domain removed successfully');
      loadDomains();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to remove domain');
    }
  };

  const handleUpdateBranding = async (domainId: number) => {
    try {
      await apiService.put(`/agencies/${agencyId}/domains/${domainId}/branding`, branding);
      showSuccess('Branding updated successfully');
      setShowBrandingForm(false);
      setSelectedDomain(null);
      setBranding({ logo_url: '', primary_color: '', favicon_url: '' });
      loadDomains();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to update branding');
    }
  };

  const openBrandingForm = (domain: AgencyDomain) => {
    setSelectedDomain(domain);
    setBranding({
      logo_url: domain.logo_url || '',
      primary_color: domain.primary_color || '',
      favicon_url: domain.favicon_url || ''
    });
    setShowBrandingForm(true);
  };

  const cancelBrandingForm = () => {
    setShowBrandingForm(false);
    setSelectedDomain(null);
    setBranding({ logo_url: '', primary_color: '', favicon_url: '' });
  };

  if (!isOpen) return null;

  // Styles
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    zIndex: 1400,
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--v2-border)',
    boxShadow: 'var(--shadow-lg)',
    width: '100%',
    maxWidth: '700px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'var(--font-sans)',
  };

  const headerStyle: React.CSSProperties = {
    padding: '24px 24px 16px',
    borderBottom: '1px solid var(--v2-border)',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: 'var(--v2-text)',
    margin: 0,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    color: 'var(--v2-text-tertiary)',
    margin: '4px 0 0 0',
  };

  const contentStyle: React.CSSProperties = {
    padding: '24px',
    overflowY: 'auto',
    flex: 1,
  };

  const footerStyle: React.CSSProperties = {
    padding: '16px 24px',
    borderTop: '1px solid var(--v2-border)',
    backgroundColor: 'var(--v2-surface-alt)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const infoBoxStyle: React.CSSProperties = {
    padding: '16px',
    borderRadius: 'var(--radius-md)',
    borderLeft: '4px solid var(--v2-primary)',
    backgroundColor: 'var(--v2-info-bg)',
    marginBottom: '24px',
    display: 'flex',
    gap: '12px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--v2-text)',
    marginBottom: '6px',
  };

  const getInputStyle = (focused: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.9375rem',
    border: focused ? '1px solid var(--v2-primary)' : '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--v2-surface)',
    color: 'var(--v2-text)',
    outline: 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    boxSizing: 'border-box',
    boxShadow: focused ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
  });

  const getPrimaryButtonStyle = (hovered: boolean, disabled: boolean): React.CSSProperties => ({
    padding: '10px 20px',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: disabled ? 'var(--v2-border)' : hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.15s ease',
    whiteSpace: 'nowrap',
  });

  const getSecondaryButtonStyle = (hovered: boolean): React.CSSProperties => ({
    padding: '10px 20px',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: hovered ? 'var(--v2-text)' : 'var(--v2-text-tertiary)',
    backgroundColor: hovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  });

  const iconButtonStyle = (hovered: boolean, danger?: boolean): React.CSSProperties => ({
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    backgroundColor: hovered ? (danger ? 'var(--v2-error-bg)' : 'var(--v2-surface-hover)') : 'transparent',
    color: hovered && danger ? 'var(--v2-error)' : 'var(--v2-text-tertiary)',
    transition: 'background-color 0.15s ease, color 0.15s ease',
  });

  const getStatusBadge = (status: string): React.CSSProperties => {
    const colors: { [key: string]: { bg: string; color: string } } = {
      'active': { bg: 'var(--v2-success-bg)', color: 'var(--v2-success)' },
      'pending_dns': { bg: 'var(--v2-warning-bg)', color: '#92400e' },
      'validating': { bg: 'var(--v2-info-bg)', color: 'var(--v2-primary)' },
      'deleting': { bg: 'var(--v2-error-bg)', color: 'var(--v2-error)' },
      'failed': { bg: 'var(--v2-error-bg)', color: 'var(--v2-error)' },
    };
    const config = colors[status] || { bg: 'var(--v2-bg)', color: 'var(--v2-text-tertiary)' };
    return {
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: 'var(--radius-full)',
      fontSize: '0.75rem',
      fontWeight: 600,
      backgroundColor: config.bg,
      color: config.color,
    };
  };

  const getStatusLabel = (status: string): string => {
    const labels: { [key: string]: string } = {
      'active': 'Active',
      'pending_dns': 'Pending DNS',
      'validating': 'Validating',
      'deleting': 'Deleting',
      'failed': 'Failed',
    };
    return labels[status] || status;
  };

  const domainCardStyle = (hovered: boolean): React.CSSProperties => ({
    backgroundColor: hovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--v2-border)',
    padding: '16px',
    marginBottom: '12px',
    transition: 'background-color 0.15s ease',
  });

  const alertErrorStyle: React.CSSProperties = {
    padding: '10px 14px',
    borderRadius: 'var(--radius-sm)',
    borderLeft: '4px solid var(--v2-error)',
    backgroundColor: 'var(--v2-error-bg)',
    color: 'var(--v2-error)',
    fontSize: '0.8125rem',
    marginTop: '12px',
  };

  const brandingFormStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface-alt)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--v2-border)',
    padding: '20px',
    marginBottom: '24px',
  };

  return ReactDOM.createPortal(
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={overlayStyle} onClick={onClose}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={headerStyle}>
            <h2 style={titleStyle}>Agency Custom Domains</h2>
            <p style={subtitleStyle}>Manage custom domains for {agencyName} management portal</p>
          </div>

          {/* Content */}
          <div style={contentStyle}>
            {/* Info Box */}
            <div style={infoBoxStyle}>
              <Info size={20} strokeWidth={1.5} color="var(--v2-primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: 'var(--v2-primary)', fontSize: '0.875rem' }}>
                  How Custom Domains Work
                </p>
                <ul style={{ margin: 0, paddingLeft: '20px', color: 'var(--v2-text-secondary)', fontSize: '0.8125rem', lineHeight: 1.8 }}>
                  <li><strong>Step 1:</strong> Add your custom domain below (e.g., dashboard.mycompany.com)</li>
                  <li><strong>Step 2:</strong> We'll create a unique subdomain for your agency (agency-xxx.dashboardfox.app)</li>
                  <li><strong>Step 3:</strong> Create a CNAME record in your DNS pointing to our subdomain</li>
                  <li><strong>Step 4:</strong> SSL is automatically handled - no certificates needed on your end</li>
                </ul>
              </div>
            </div>

            {/* Add Domain Form */}
            {!showBrandingForm && (
              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>Add New Domain</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      value={newDomain}
                      onChange={(e) => setNewDomain(e.target.value)}
                      onFocus={() => setFocusedInput('newDomain')}
                      onBlur={() => setFocusedInput(null)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddDomain()}
                      placeholder="dashboard.mycompany.com"
                      disabled={adding}
                      style={getInputStyle(focusedInput === 'newDomain')}
                    />
                  </div>
                  <button
                    onClick={handleAddDomain}
                    onMouseEnter={() => setHoveredButton('add')}
                    onMouseLeave={() => setHoveredButton(null)}
                    disabled={adding || !newDomain.trim()}
                    style={getPrimaryButtonStyle(hoveredButton === 'add', adding || !newDomain.trim())}
                  >
                    {adding ? 'Adding...' : 'Add'}
                  </button>
                </div>
                <p style={{ margin: '6px 0 0 0', fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>
                  Enter the custom domain you want to use for this agency's management portal. The domain must be managed in Cloudflare.
                </p>
              </div>
            )}

            {/* Branding Form */}
            {showBrandingForm && selectedDomain && (
              <div style={brandingFormStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: 'var(--radius-sm)',
                    backgroundColor: 'var(--v2-primary-light)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Palette size={18} strokeWidth={1.5} color="var(--v2-primary)" />
                  </div>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--v2-text)' }}>
                    Update Branding for {selectedDomain.domain}
                  </h3>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <label style={labelStyle}>Logo URL</label>
                    <input
                      type="text"
                      value={branding.logo_url}
                      onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })}
                      onFocus={() => setFocusedInput('logo')}
                      onBlur={() => setFocusedInput(null)}
                      placeholder="https://example.com/logo.png"
                      style={getInputStyle(focusedInput === 'logo')}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Primary Color (Hex)</label>
                    <input
                      type="text"
                      value={branding.primary_color}
                      onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                      onFocus={() => setFocusedInput('color')}
                      onBlur={() => setFocusedInput(null)}
                      placeholder="#3B82F6"
                      style={getInputStyle(focusedInput === 'color')}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Favicon URL</label>
                    <input
                      type="text"
                      value={branding.favicon_url}
                      onChange={(e) => setBranding({ ...branding, favicon_url: e.target.value })}
                      onFocus={() => setFocusedInput('favicon')}
                      onBlur={() => setFocusedInput(null)}
                      placeholder="https://example.com/favicon.ico"
                      style={getInputStyle(focusedInput === 'favicon')}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => handleUpdateBranding(selectedDomain.id)}
                      onMouseEnter={() => setHoveredButton('saveBranding')}
                      onMouseLeave={() => setHoveredButton(null)}
                      style={getPrimaryButtonStyle(hoveredButton === 'saveBranding', false)}
                    >
                      Save Branding
                    </button>
                    <button
                      onClick={cancelBrandingForm}
                      onMouseEnter={() => setHoveredButton('cancelBranding')}
                      onMouseLeave={() => setHoveredButton(null)}
                      style={getSecondaryButtonStyle(hoveredButton === 'cancelBranding')}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Domains List */}
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0', gap: '12px' }}>
                <Loader2 size={32} strokeWidth={2} color="var(--v2-primary)" style={{ animation: 'spin 1s linear infinite' }} />
                <p style={{ margin: 0, color: 'var(--v2-text-tertiary)' }}>Loading domains...</p>
              </div>
            ) : domains.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <p style={{ margin: 0, color: 'var(--v2-text-tertiary)' }}>No custom domains configured yet.</p>
              </div>
            ) : (
              <div>
                {domains.map((domain) => (
                  <div
                    key={domain.id}
                    style={domainCardStyle(hoveredDomain === domain.id)}
                    onMouseEnter={() => setHoveredDomain(domain.id)}
                    onMouseLeave={() => setHoveredDomain(null)}
                  >
                    {/* Domain Header */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--v2-text)' }}>{domain.domain}</span>
                        <span style={getStatusBadge(domain.status)}>{getStatusLabel(domain.status)}</span>
                        {domain.cloudflare_proxy_enabled && (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 8px',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: '0.6875rem',
                            fontWeight: 500,
                            backgroundColor: 'var(--v2-bg)',
                            color: 'var(--v2-text-tertiary)',
                            border: '1px solid var(--v2-border)',
                          }}>
                            <Shield size={12} strokeWidth={1.5} /> CF Proxy
                          </span>
                        )}
                      </div>
                      
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => openBrandingForm(domain)}
                          onMouseEnter={() => setHoveredButton(`brand-${domain.id}`)}
                          onMouseLeave={() => setHoveredButton(null)}
                          style={iconButtonStyle(hoveredButton === `brand-${domain.id}`)}
                          title="Update branding"
                        >
                          <Palette size={16} strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => handleRemoveDomain(domain.id)}
                          onMouseEnter={() => setHoveredButton(`delete-${domain.id}`)}
                          onMouseLeave={() => setHoveredButton(null)}
                          style={iconButtonStyle(hoveredButton === `delete-${domain.id}`, true)}
                          title="Remove domain"
                        >
                          <Trash2 size={16} strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>

                    {/* Domain Details */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '0.8125rem' }}>
                      <div>
                        <span style={{ color: 'var(--v2-text-tertiary)' }}>DNS Validated: </span>
                        <span style={{ color: 'var(--v2-text)' }}>
                          {domain.dns_validated_at 
                            ? new Date(domain.dns_validated_at).toLocaleString()
                            : 'Not yet'}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: 'var(--v2-text-tertiary)' }}>SSL Issued: </span>
                        <span style={{ color: 'var(--v2-text)' }}>
                          {domain.ssl_issued_at 
                            ? new Date(domain.ssl_issued_at).toLocaleString()
                            : 'Pending'}
                        </span>
                      </div>
                    </div>

                    {/* Validation Errors */}
                    {domain.validation_errors && (
                      <div style={alertErrorStyle}>
                        {domain.validation_errors}
                      </div>
                    )}

                    {/* Branding Info */}
                    {(domain.logo_url || domain.primary_color || domain.favicon_url) && (
                      <div style={{
                        marginTop: '12px',
                        padding: '10px 14px',
                        backgroundColor: 'var(--v2-surface-alt)',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--v2-border)',
                      }}>
                        <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--v2-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Branding
                        </span>
                        <div style={{ display: 'flex', gap: '16px', marginTop: '6px', fontSize: '0.8125rem', color: 'var(--v2-text)' }}>
                          {domain.logo_url && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              <Check size={12} strokeWidth={2} color="var(--v2-success)" /> Logo
                            </span>
                          )}
                          {domain.primary_color && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              <Check size={12} strokeWidth={2} color="var(--v2-success)" /> Color: {domain.primary_color}
                            </span>
                          )}
                          {domain.favicon_url && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              <Check size={12} strokeWidth={2} color="var(--v2-success)" /> Favicon
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={footerStyle}>
            <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)' }}>
              {domains.length} domain{domains.length !== 1 ? 's' : ''} configured
            </span>
            <button
              onClick={onClose}
              onMouseEnter={() => setHoveredButton('close')}
              onMouseLeave={() => setHoveredButton(null)}
              style={getSecondaryButtonStyle(hoveredButton === 'close')}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default AgencyDomainsModal;
